import { useCallback, useEffect } from 'react';
import toNumber from 'lodash/toNumber';

import { ProfileInputEditableFieldValidationProps } from './useProfileInputEditableField.types';

import { useReactHookForm } from '../../../../../common/hooks/useReactHookForm';

interface ProfileInputEditableFieldProps {
  fieldName: string;
  defaultValue?: string;
  onSubmit?: (value: string | number) => Promise<unknown>;
  type?: string;
}

function useProfileInputEditableField({
  fieldName,
  defaultValue,
  onSubmit,
  type,
  validationSchema,
  validationRules
}: ProfileInputEditableFieldProps & ProfileInputEditableFieldValidationProps) {
  const {
    control,
    errors,
    handleSubmitReactHookForm,
    register,
    resetForm,
    watch,
    trigger,
    setValue,
    getValues
  } = useReactHookForm({
    defaultValues: {
      [fieldName]: defaultValue
    },
    schema: validationSchema
  });

  useEffect(() => {
    if (defaultValue !== getValues(fieldName)) {
      setValue(fieldName, defaultValue);
    }
  }, [defaultValue, fieldName, setValue, getValues]);

  return {
    validationErrors: errors[fieldName]?.message,
    control,
    resetForm: useCallback<() => void>(
      () =>
        resetForm({
          [fieldName]: defaultValue
        }),
      [defaultValue, fieldName, resetForm]
    ),
    handleUpdate: handleSubmitReactHookForm({
      onSubmit: (data) => {
        if (type === 'number') {
          return onSubmit(data[fieldName] ? toNumber(data[fieldName]) : null);
        }

        return onSubmit(data[fieldName]);
      },
      dirtyFieldsOnly: false
    }),
    registerField: register(fieldName, validationRules),
    getValueField: watch(fieldName),
    trigger
  };
}

export default useProfileInputEditableField;
